import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Field } from 'react-final-form';
import { differenceInDays, parseISO } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { Unstable_Grid2 as Grid, Alert } from '@mui/material';
import DatePickerFieldAdapter from 'components/shared/field-adapters/DatePickerFieldAdapter';
import HourSelectFieldAdapter from 'components/shared/field-adapters/hour-picker-field-adapter/HourPickerFieldAdapter';
import SelectFieldAdapter from 'components/shared/field-adapters/SelectFieldAdapter';
import TimeFieldAdapter from 'components/shared/field-adapters/TimeFieldAdapter';
import ActivePaymentSelector from 'components/session/fields/ActivePaymentSelector';
import { getLanguageOptions, getSessionStatusOptions } from 'common/constants/options';
import { isAdmin, isClient } from 'common/utils/user';

function SessionFieldsSessionDetails({ isEditing, values }) {
  const { t } = useTranslation(['labels', 'sessions']);

  const role = useSelector((state) => state.auth.user.role);
  const isActivePlus = useSelector((state) => state.auth.user.is_active_plus);

  const activePlusEndDate = useSelector((state) => state.auth.user.active_plus_end_date ?? null);

  const isAdminUser = isAdmin(role);
  const isActivePlusClient = isClient(role) && isActivePlus;
  const activePlusDaysLeft = activePlusEndDate
    ? Math.max(0, differenceInDays(parseISO(activePlusEndDate), new Date()))
    : 0;

  // Estado local para el pago seleccionado
  const [selectedPayment, setSelectedPayment] = useState(null);

  // Obtener los pagos activos del Redux state
  const activePayments = useSelector((state) => state.clientPayment.data.activePayments) || [];

  // Manejar el cambio de pago seleccionado
  const handlePaymentChange = (payment) => {
    setSelectedPayment(payment);
  };

  // Obtener categorías filtradas por el plan seleccionado
  // Buscar el pago seleccionado tanto en el estado local como en Redux.
  // La comparación va por String: el id llega de GraphQL como `ID` (cadena) pero por el formulario
  // puede venir como número, y con `===` estricto nunca casaban. Al editar una sesión eso dejaba
  // `currentPayment` en undefined, las categorías en lista vacía y el campo deshabilitado.
  const currentPayment =
    selectedPayment || activePayments.find((p) => String(p.id) === String(values.payment_id));

  const sessionTypeOptions = currentPayment && currentPayment.plan_id && currentPayment.plan_id.session_types
    ? currentPayment.plan_id.session_types.map((type) => ({
        value: parseInt(type.id, 10),
        label: type.name
      }))
    : [];

  // Obtener slots y metadata del Redux state
  const availableTimeSlots = useSelector(
    (state) => state.trainer.availableTimeSlots.data || []
  );

  const show24hNotice = useSelector(
    (state) => state.trainer.availableTimeSlots.show24hNotice || false
  );

  const slotsStatus = useSelector(
    (state) => state.trainer.availableTimeSlots.status
  );

  // Determinar si hay entrenadores disponibles
  const hasAvailableTrainers = Array.isArray(availableTimeSlots) && availableTimeSlots.length > 0;

  // Solo mostrar el warning si ya se completó la búsqueda y no hay resultados
  const hasCompletedSearch = slotsStatus === 'success' || slotsStatus === 'error';
  const shouldShowNoTrainersWarning = hasCompletedSearch && !hasAvailableTrainers && values.date && values.session_type_id && values.language;


  // Crear opciones de tiempo basadas SOLO en los slots disponibles
  const timeOptions = useMemo(() => {
    const baseDate = values?.date || new Date();

    // Si no hay slots disponibles o el estado está cargando, retornar array vacío
    if (!hasAvailableTrainers || !Array.isArray(availableTimeSlots) || slotsStatus === 'loading') {
      return [];
    }

    // Crear un mapa de horas disponibles con sus opciones completas
    const availableHoursMap = new Map();

    availableTimeSlots.forEach((slot) => {
      // Validar que el slot tenga start_time
      if (!slot || !slot.start_time) {
        return;
      }

      try {
        // El backend envía en UTC, convertir a hora local del usuario
        const utcTime = new Date(slot.start_time);
        if (Number.isNaN(utcTime.getTime())) return;
        const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const startTime = utcToZonedTime(utcTime, userTimezone);

        const hour = startTime.getHours();
        const minute = startTime.getMinutes();

        // Crear la fecha/hora con la hora Bogotá en la fecha base
        const localDateTime = new Date(baseDate);
        localDateTime.setHours(hour, minute, 0, 0);

        // Usar la hora como key para evitar duplicados
        const key = `${hour}:${minute.toString().padStart(2, '0')}`;

        if (!availableHoursMap.has(key)) {
          availableHoursMap.set(key, {
            value: localDateTime,
            label: localDateTime.toLocaleTimeString('es-CO', {
              hour: '2-digit',
              minute: '2-digit',
              hour12: true
            })
          });
        }
      } catch (error) {
        // Silenciar errores de slots inválidos
      }
    });

    // Convertir el map a array y ordenar por hora
    return Array.from(availableHoursMap.values()).sort((a, b) =>
      a.value.getTime() - b.value.getTime()
    );
  }, [values.date, availableTimeSlots, hasAvailableTrainers]);

  return (
    <Grid container spacing={3}>
      {/* Info para usuarios Active Plus */}
      {isActivePlusClient && (
        <Grid xs={12}>
          <Alert severity="info">
            {t('activePlusUserInfo', { ns: 'sessions', days: activePlusDaysLeft })}
          </Alert>
        </Grid>
      )}

      {/* Alerta de 24 horas - Mostrar si show24hNotice es true */}
      {show24hNotice && (
        <Grid xs={12}>
          <Alert severity="info">
            Solo es posible agendar sesiones con al menos 24 horas de anticipación.
          </Alert>
        </Grid>
      )}

      {/* Warning cuando no hay entrenadores disponibles */}
      {shouldShowNoTrainersWarning && (
        <Grid xs={12}>
          <Alert severity="warning">
            No hay entrenadores u horarios disponibles para la fecha y categoría seleccionada. Intenta con otra fecha, categoría o idioma.
          </Alert>
        </Grid>
      )}

      {/* Primera fila: Plan y Categoría */}
      <Grid xs={12} md={6}>
        <ActivePaymentSelector
          clientId={values.client_id?.value}
          disabled={!values.client_id}
          onChange={handlePaymentChange}
          currentPayment={
            values.payment_id && values.payment_label
              ? { id: values.payment_id, label: values.payment_label }
              : null
          }
        />
      </Grid>

      <Grid xs={12} md={6}>
        <Field
          id="session_type_id"
          name="session_type_id"
          component={SelectFieldAdapter}
          options={sessionTypeOptions}
          label={t('category', { ns: 'labels' })}
          fullWidth
          required
          disabled={!values.payment_id || sessionTypeOptions.length === 0}
        />
      </Grid>

      {/* Segunda fila: Idioma y Estado (si es admin) */}
      <Grid xs={12} md={isAdminUser ? 6 : 12}>
        <Field
          id="language"
          name="language"
          component={SelectFieldAdapter}
          options={getLanguageOptions(t)}
          label={t('sessionLanguage', { ns: 'labels' })}
          fullWidth
          required
          disabled={!values.session_type_id}
        />
      </Grid>
      {isAdminUser && (
        <Grid xs={12} md={6}>
          <Field
            id="status"
            name="status"
            component={SelectFieldAdapter}
            options={getSessionStatusOptions(t)}
            label={t('status', { ns: 'labels' })}
            fullWidth
            required
            disabled={!values.session_type_id}
          />
        </Grid>
      )}

      {/* Tercera fila: Fecha, Desde, Hasta */}
      <Grid xs={12} md={4}>
        <Field
          id="date"
          name="date"
          component={DatePickerFieldAdapter}
          label={t('date', { ns: 'labels' })}
          disabled={!values.payment_id || !values.session_type_id}
          disablePast={!isEditing}
          maxDate={currentPayment ? new Date(currentPayment.end_date) : null}
          minDate={currentPayment ? new Date(currentPayment.start_date) : null}
          renderInputProps={{
            fullWidth: true,
            required: true
          }}
        />
      </Grid>
      <Grid xs={6} md={4}>
        <Field
          id="from"
          name="from"
          component={HourSelectFieldAdapter}
          label={t('from', { ns: 'common' })}
          dateFormat="hh aaa"
          disablePast
          options={timeOptions}
          disabled={!values.date || !values.session_type_id || !values.language || !hasAvailableTrainers}
          referenceDate={values.date}
          renderInputProps={{
            fullWidth: true,
            required: true
          }}
        />
      </Grid>
      <Grid xs={6} md={4}>
        <Field
          id="to"
          name="to"
          component={TimeFieldAdapter}
          label={t('to', { ns: 'common' })}
          dateFormat="hh aaa"
          referenceDate={values.date}
          renderInputProps={{
            fullWidth: true,
            required: true,
            disabled: true
          }}
        />
      </Grid>
    </Grid>
  );
}

SessionFieldsSessionDetails.propTypes = {
  isEditing: PropTypes.bool.isRequired,
  values: PropTypes.object.isRequired
};

export default SessionFieldsSessionDetails;
